import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ExternalLink, Github, Search } from "lucide-react";
import api from "@/lib/api";
import Navbar from "@/components/public/Navbar";
import Footer from "@/components/public/Footer";
import BackToTop from "@/components/public/BackToTop";

const statusColor = (s) => {
    if (s === "Active") return "bg-emerald-500/15 border-emerald-500/40 text-emerald-300";
    if (s === "Maintenance") return "bg-amber-500/15 border-amber-500/40 text-amber-300";
    if (s === "Archived") return "bg-zinc-500/15 border-zinc-500/40 text-zinc-300";
    return "bg-white/5 border-white/15 text-ink-100";
};

export default function Work() {
    const [projects, setProjects] = useState([]);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [settings, setSettings] = useState(null);
    const [activeCategory, setActiveCategory] = useState("All");
    const [activeStatus, setActiveStatus] = useState("All");

    useEffect(() => {
        Promise.all([api.get("/projects"), api.get("/profile"), api.get("/settings")])
            .then(([p, pr, st]) => {
                setProjects(p.data);
                setProfile(pr.data);
                setSettings(st.data);
            })
            .finally(() => setLoading(false));
    }, []);

    const categories = useMemo(() => ["All", ...Array.from(new Set(projects.map((p) => p.category)))], [projects]);
    const statuses = useMemo(() => ["All", ...Array.from(new Set(projects.map((p) => p.status).filter(Boolean)))], [projects]);

    const filtered = projects.filter((p) => {
        const q = query.trim().toLowerCase();
        const matchQ = !q || [p.title, p.summary, p.description, p.client, p.category, ...(p.tech_stack || [])]
            .filter(Boolean)
            .some((t) => String(t).toLowerCase().includes(q));
        const matchC = activeCategory === "All" || p.category === activeCategory;
        const matchS = activeStatus === "All" || p.status === activeStatus;
        return matchQ && matchC && matchS;
    });

    return (
        <div data-testid="work-page" className="min-h-screen bg-ink-900 text-white">
            <Navbar profile={profile} settings={settings} />
            <main className="pt-32 pb-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <Link
                        to="/"
                        data-testid="work-back"
                        className="inline-flex items-center gap-2 text-ink-200 hover:text-brand transition-colors mb-10"
                    >
                        <ArrowLeft size={16} /> Back home
                    </Link>

                    <div className="mb-12">
                        <div className="mono-label mb-4">Archive · All Work</div>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[0.92] tracking-tighter">
                            All projects.
                            <br />
                            <span className="text-brand">One place.</span>
                        </h1>
                        <p className="mt-6 text-ink-200 max-w-2xl leading-relaxed">
                            Comprehensive engineering archive of {projects.length} projects — spanning enterprise banking, scalable commerce,
                            automated middleware, and public sector platforms. Search, filter, and explore.
                        </p>
                    </div>

                    {/* Filters */}
                    <div className="mb-10 space-y-4">
                        <div className="relative max-w-xl">
                            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-300" />
                            <input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search by title, tech, client…"
                                data-testid="work-search"
                                className="w-full bg-ink-800/60 border border-white/10 rounded-full pl-11 pr-4 py-3 text-white placeholder:text-ink-300 focus:outline-none focus:border-brand/60 focus:ring-4 focus:ring-brand/10"
                            />
                        </div>
                        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none sm:flex-wrap sm:overflow-visible sm:pb-0" data-testid="work-categories">
                            {categories.map((c) => (
                                <button
                                    key={c}
                                    onClick={() => setActiveCategory(c)}
                                    data-testid={`work-category-${c.toLowerCase().replace(/\s|\//g, "-")}`}
                                    className={`shrink-0 px-3.5 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-wider border transition-colors ${
                                        activeCategory === c
                                            ? "bg-brand border-brand text-white"
                                            : "border-white/10 text-ink-200 hover:border-white/30 hover:text-white"
                                    }`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none sm:flex-wrap sm:overflow-visible sm:pb-0" data-testid="work-statuses">
                            {statuses.map((s) => (
                                <button
                                    key={s}
                                    onClick={() => setActiveStatus(s)}
                                    data-testid={`work-status-${s.toLowerCase()}`}
                                    className={`shrink-0 px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-wider border transition-colors ${
                                        activeStatus === s
                                            ? "bg-white text-black border-white"
                                            : "border-white/10 text-ink-300 hover:text-white hover:border-white/30"
                                    }`}
                                >
                                    {s === "All" ? "All status" : s}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="mb-6 mono-label">
                        {loading ? "Loading…" : `Showing ${filtered.length} of ${projects.length} projects`}
                    </div>

                    {/* Grid */}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {filtered.map((p) => (
                            <Link
                                key={p.id || p.slug}
                                to={`/projects/${p.slug}`}
                                data-testid={`work-card-${p.slug}`}
                                className="group rounded-xl border border-white/5 bg-ink-800/40 overflow-hidden hover:border-brand/40 hover:-translate-y-1 transition-all"
                            >
                                <div className="relative aspect-[16/10] bg-ink-900 overflow-hidden">
                                    {p.cover_image && (
                                        <img
                                            src={p.cover_image}
                                            alt={p.title}
                                            loading="lazy"
                                            decoding="async"
                                            className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                                        />
                                    )}
                                    <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/40 to-transparent" />
                                    <div className="absolute top-3 left-3 flex flex-wrap items-center gap-1.5">
                                        <span className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-black/60 backdrop-blur border border-white/10 text-ink-100">
                                            {p.category}
                                        </span>
                                        {p.client?.includes("Kodekami") && (
                                            <span className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-brand/25 border border-brand/50 text-brand-light backdrop-blur font-medium">
                                                ⚡ Kodekami
                                            </span>
                                        )}
                                        {p.status && (
                                            <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full border ${statusColor(p.status)}`}>
                                                {p.status}
                                            </span>
                                        )}
                                    </div>
                                    <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white text-black flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                        <ArrowUpRight size={14} />
                                    </div>
                                </div>
                                <div className="p-5 space-y-2">
                                    <div className="flex items-center justify-between mono-label">
                                        <span>
                                            {p.year} · {p.client?.includes("Kodekami") ? (
                                                <span className="text-brand font-medium">{p.client}</span>
                                            ) : (
                                                p.client
                                            )}
                                        </span>
                                        <div className="flex gap-1.5">
                                            {p.demo_url && <ExternalLink size={11} className="text-ink-300 group-hover:text-brand transition-colors" />}
                                            {p.repo_url && <Github size={11} className="text-ink-300 group-hover:text-brand transition-colors" />}
                                        </div>
                                    </div>
                                    <h3 className="font-display text-lg text-white tracking-tight leading-tight line-clamp-2">{p.title}</h3>
                                    <p className="text-sm text-ink-200 line-clamp-2 leading-relaxed">{p.summary}</p>
                                </div>
                            </Link>
                        ))}
                    </div>

                    {filtered.length === 0 && !loading && (
                        <div className="text-center py-20 text-ink-300">
                            No projects match your filters.{" "}
                            <button
                                onClick={() => {
                                    setQuery("");
                                    setActiveCategory("All");
                                    setActiveStatus("All");
                                }}
                                className="text-brand underline"
                            >
                                Clear all
                            </button>
                        </div>
                    )}
                </div>
            </main>
            <Footer profile={profile} />
            <BackToTop />
        </div>
    );
}
